import { useState } from 'react';
import type { Identity, KeyCreateIn, TeamSummary } from '../types';

interface CreateKeyFormProps {
  teams: TeamSummary[];
  identity: Identity | null;
  onSubmit: (data: KeyCreateIn) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

const DEFAULT_EXPIRY_DAYS = 90;
const MAX_EXPIRY_DAYS = 90;

/**
 * Key creation form with mandatory expiry capped at 90 days. Expiry is immutable
 * upstream, so extending it requires issuing a new key rather than editing this one.
 * Only shows teams the user actually belongs to (lead or member). An uncapped key is
 * offered only to a lead of the selected team or a platform admin; the API enforces
 * the same rule.
 */
export function CreateKeyForm({
  teams,
  identity,
  onSubmit,
  onCancel,
  submitting,
}: CreateKeyFormProps) {
  const [teamId, setTeamId] = useState(teams.length === 1 ? teams[0]!.id : '');
  const [label, setLabel] = useState('');
  const [limitEnabled, setLimitEnabled] = useState(true);
  const [limitUsd, setLimitUsd] = useState('');
  const [limitReset, setLimitReset] = useState<'daily' | 'weekly' | 'monthly' | null>('monthly');
  const [expiryDays, setExpiryDays] = useState(String(DEFAULT_EXPIRY_DAYS));
  const [error, setError] = useState<string | null>(null);

  const mayUncap = canCreateUncappedKey(teamId, identity);
  // A member can never submit an uncapped key, even if they unticked the box under a
  // team they lead and then switched team.
  const hasLimit = limitEnabled || !mayUncap;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!teamId) {
      setError('Please select a team');
      return;
    }
    if (!label.trim()) {
      setError('Label is required');
      return;
    }

    const expiryDaysNum = parseInt(expiryDays, 10);
    if (isNaN(expiryDaysNum) || expiryDaysNum <= 0) {
      setError('Expiry must be a positive number of days');
      return;
    }
    if (expiryDaysNum > MAX_EXPIRY_DAYS) {
      setError(`Expiry beyond ${MAX_EXPIRY_DAYS} days requires platform admin approval`);
      return;
    }

    let limitValue: number | null = null;
    if (hasLimit) {
      const parsed = parseFloat(limitUsd);
      if (isNaN(parsed) || parsed < 0) {
        setError('Limit must be a non-negative number');
        return;
      }
      limitValue = parsed;
    }

    const data: KeyCreateIn = {
      team_id: teamId,
      label: label.trim(),
      limit_usd: limitValue,
      limit_reset: hasLimit ? limitReset : null,
      expires_in_days: expiryDaysNum,
    };

    try {
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="create-key-form">
      <h3>Create a new key</h3>

      <div className="form-group">
        <label htmlFor="team">
          Team <span className="required">*</span>
        </label>
        <select
          id="team"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          disabled={submitting || teams.length === 1}
          required
        >
          {teams.length === 0 ? (
            <option value="">No teams available</option>
          ) : (
            <>
              {teams.length > 1 && <option value="">Select a team...</option>}
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </>
          )}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="label">
          Label <span className="required">*</span>
        </label>
        <input
          id="label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={submitting}
          placeholder="e.g., ci-runner, batch-eval"
          required
        />
        <p className="form-note">Free text. This becomes part of the OpenRouter key name.</p>
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={hasLimit}
            onChange={(e) => setLimitEnabled(e.target.checked)}
            disabled={submitting || !mayUncap}
          />
          Set a spending limit
        </label>
        {!mayUncap ? (
          <p className="form-note">
            A spending limit is required. Only a team lead or platform admin can create a key
            with no limit.
          </p>
        ) : null}
        {hasLimit ? (
          <>
            <div className="form-inline">
              <input
                type="number"
                value={limitUsd}
                onChange={(e) => setLimitUsd(e.target.value)}
                disabled={submitting}
                placeholder="0.00"
                step="0.01"
                min="0"
                style={{ width: '120px' }}
              />
              <span>USD per</span>
              <select
                value={limitReset ?? 'monthly'}
                onChange={(e) =>
                  setLimitReset(e.target.value as 'daily' | 'weekly' | 'monthly' | null)
                }
                disabled={submitting}
              >
                <option value="daily">day</option>
                <option value="weekly">week</option>
                <option value="monthly">month</option>
              </select>
            </div>
            <p className="form-note warn">
              Limits can only be decreased later. Increases go through a temporary budget increase request, which needs approval.
            </p>
          </>
        ) : (
          <p className="form-note">No limit — spend is uncapped for this key.</p>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="expiry">
          Expires in <span className="required">*</span>
        </label>
        <div className="form-inline">
          <input
            id="expiry"
            type="number"
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            disabled={submitting}
            min="1"
            max={MAX_EXPIRY_DAYS}
            style={{ width: '80px' }}
            required
          />
          <span>days (max {MAX_EXPIRY_DAYS})</span>
        </div>
        <p className="form-note warn">
          Expiry is mandatory and capped at {MAX_EXPIRY_DAYS} days without platform admin approval.
          It cannot be extended later — a longer window requires issuing a new key.
        </p>
      </div>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={submitting || teams.length === 0}>
          {submitting ? 'Creating...' : 'Create key'}
        </button>
      </div>
    </form>
  );
}

/** Lead of the selected team, or a platform admin. Mirrors `may_create_uncapped_key`. */
export function canCreateUncappedKey(teamId: string, identity: Identity | null): boolean {
  if (!identity) return false;
  if (identity.is_platform_admin) return true;
  if (!teamId) return false;
  return identity.teams.some((t) => t.team_id === teamId && t.role === 'lead');
}
